import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SERVICES } from '../lib/site';
import { TEAM_WIDE_BANNER, imageForServiceKey, slotForServiceKey } from '../lib/imagery';
import { useBrandImage } from '../lib/BrandImagesContext';
import { useJsonLd, breadcrumbSchema, siteOrigin } from '../lib/seo';

function ServiceRow({ s, i }) {
    const Icon = s.icon;
    const reverse = i % 2 === 1;
    const img = useBrandImage(slotForServiceKey(s.key), imageForServiceKey(s.key));
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}
        >
            <div className={`lg:col-span-6 ${reverse ? 'lg:[direction:ltr]' : ''}`}>
                <span className="font-mono text-[11px] tracking-[0.22em] text-primary">{s.tag.toUpperCase()}</span>
                <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                    {s.title}
                </h2>
                <p className="mt-3 text-foreground/80 leading-relaxed max-w-lg">{s.body}</p>
                <ul className="mt-5 space-y-2 text-sm text-foreground/85">
                    {(s.proof || [
                        'Senior-led delivery teams',
                        'Production-grade architecture from day one',
                        'Security and privacy engineered in',
                    ]).map((b) => (
                        <li key={b} className="flex items-start gap-2"><ChevronRight className="h-4 w-4 text-primary mt-0.5" /> {b}</li>
                    ))}
                </ul>
            </div>
            <div className={`lg:col-span-6 ${reverse ? 'lg:[direction:ltr]' : ''}`}>
                <div className="dt-glass rounded-2xl relative overflow-hidden min-h-[260px]">
                    <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                            src={img}
                            alt=""
                            aria-hidden
                            loading="lazy"
                            className="w-full h-full object-cover"
                            style={{ filter: 'saturate(0.6) hue-rotate(-6deg) brightness(0.65)' }}
                            onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,12,20,0.98)] via-[rgba(8,12,20,0.55)] to-[rgba(8,12,20,0.1)]" />
                    </div>
                    <div className="p-8 lg:p-10 relative -mt-10">
                        <div className="absolute -top-12 -right-10 h-56 w-56 rounded-full bg-[radial-gradient(closest-side,rgba(0,229,255,0.16),transparent)]" />
                        <div className="absolute -bottom-16 -left-12 h-64 w-64 rounded-full bg-[radial-gradient(closest-side,rgba(72,255,175,0.12),transparent)]" />
                        <div className="relative">
                            <span className="h-12 w-12 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary backdrop-blur-md">
                                <Icon className="h-6 w-6" />
                            </span>
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                {['Discovery', 'Architecture', 'Build', 'Operate'].map((p) => (
                                    <div key={p} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-mono uppercase tracking-widest text-foreground/85">
                                        {p}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

const FAQS = [
    {
        q: 'How do you begin a new engagement?',
        a: 'We begin by understanding the challenge, users, desired outcomes, existing systems, constraints, and success measures. We then recommend an appropriate discovery, architecture, delivery, and operating approach with clear scope, responsibilities, milestones, and governance.'
    },
    {
        q: 'Do you work with governments and regulated organisations?',
        a: 'Yes. We design solutions for public institutions, enterprises, and regulated environments. Security, privacy, auditability, access control, data handling, and resilience are addressed according to the project’s applicable legal, sector, organisational, and contractual requirements.'
    },
    {
        q: 'Can you work alongside our existing team?',
        a: 'Yes. We can provide advisory support, a dedicated engineering team, phased delivery, or collaboration with your internal and external teams. Our approach includes documentation, transparent governance, knowledge transfer, and shared ownership of outcomes.'
    },
    {
        q: 'How is pricing structured?',
        a: 'Pricing depends on scope, complexity, integrations, security requirements, delivery model, timeline, and ongoing operational support. Following initial discovery, we provide a clear commercial proposal using the most suitable model—fixed scope, phased delivery, dedicated team, managed service, or strategic partnership.'
    },
];

export default function ServicesPage() {
    const banner = useBrandImage('services.banner', TEAM_WIDE_BANNER);
    const origin = siteOrigin();

    // ItemList of services + a top-level Service schema for each service
    useJsonLd('services-itemlist', {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${origin}/services#itemlist`,
        name: 'DigiTech Cloud AI · Services',
        itemListElement: SERVICES.map((s, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@type': 'Service',
                '@id': `${origin}/services#${s.key}`,
                name: s.title,
                description: s.body,
                serviceType: s.tag,
                provider: { '@id': `${origin}/#organization` },
                areaServed: [
                    'Africa', 'United States', 'United Kingdom',
                    'European Union', 'Middle East', 'Asia',
                ],
            },
        })),
    });

    useJsonLd('services-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${origin}/services#faq`,
        mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
    });

    useJsonLd('services-breadcrumb', breadcrumbSchema([
        { name: 'Home',     url: `${origin}/` },
        { name: 'Services', url: `${origin}/services` },
    ]));

    return (
        <div data-testid="services-page">
            <section className="relative pt-32 lg:pt-36 pb-12 lg:pb-16">
                <div className="absolute inset-0 -z-10 dt-grid-overlay dt-noise pointer-events-none" />
                <div
                    aria-hidden
                    className="absolute inset-0 -z-20 pointer-events-none"
                    style={{
                        backgroundImage: `url(${banner})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 30%',
                        filter: 'saturate(0.55) hue-rotate(-8deg) brightness(0.55)',
                        opacity: 0.22,
                    }}
                />
                <div
                    aria-hidden
                    className="absolute inset-0 -z-20 pointer-events-none bg-gradient-to-b from-[rgba(8,12,20,0.9)] via-[rgba(8,12,20,0.75)] to-[rgba(8,12,20,1)]"
                />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="dt-section-eyebrow">SERVICES</span>
                    <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-4xl">
                        End-to-end engineering for systems that must <span className="dt-gradient-text">work, scale, and endure</span>.
                    </h1>
                    <p className="mt-5 text-muted-foreground max-w-2xl">
                        We combine strategy, architecture, advanced programming, AI, data, cloud, cybersecurity, integration, and operational support to build digital systems around each client’s real objectives and constraints.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_0_24px_rgba(0,229,255,0.18)]">
                            <Link to="/contact" className="inline-flex items-center gap-1.5">Book a Consultation <ArrowUpRight className="h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
                    {SERVICES.map((s, i) => <ServiceRow key={s.key} s={s} i={i} />)}
                </div>
            </section>

            <section className="py-16 lg:py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="dt-section-eyebrow">FAQ</span>
                    <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
                        How we work
                    </h2>
                    <Accordion type="single" collapsible className="mt-8">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={f.q} value={`item-${i}`} className="border-white/10">
                                <AccordionTrigger className="text-left text-base hover:no-underline">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-foreground/80 leading-relaxed">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </div>
    );
}
